import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Chip,
  Grid,
  Link,
} from '@mui/material';
import { Verified, Refresh, ArrowBack, Timer } from '@mui/icons-material';
import ResetPwdService from '../service/ResetPwdService';

const OTPVerification = ({ onSuccess, onBack, resetData, updateResetData }) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes
  const inputRefs = useRef([]);

  // Timer countdown
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setAlert({ 
        severity: 'error', 
        message: 'Le code a expiré. Veuillez demander un nouveau code.' 
      });
    }
  }, [timeLeft]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleOtpChange = (value, index) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text');
    
    if (/^\d{6}$/.test(pasteData)) {
      const newOtp = pasteData.split('');
      setOtp(newOtp);
      inputRefs.current[5]?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      setAlert({ severity: 'error', message: 'Veuillez saisir le code à 6 chiffres' });
      return;
    }

    setLoading(true);
    setAlert(null);

    try {
      const data = await ResetPwdService.verifyOtp({
        sessionId: resetData.sessionId,
        otp: otpString
      });

      if (data?.success) {
        setAlert({ severity: 'success', message: data.message });
        
        setTimeout(() => {
          onSuccess();
        }, 1500);
      } else {
        setAlert({ severity: 'error', message: data.message || 'Code invalide' });
        setOtp(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
      
    } catch (error) {
      console.error('Erreur:', error);
      setAlert({ severity: 'error', message: 'Erreur de connexion. Veuillez réessayer.' });
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    setAlert(null);

    try {
      const data = await ResetPwdService.verifyEmail(resetData?.email);
  
      if (data?.success) {
        updateResetData({ sessionId: data?.sessionId });
        setAlert({ severity: 'success', message: 'Nouveau code envoyé !' });
        setTimeLeft(300);
        setOtp(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      } else {
        setAlert({ severity: 'error', message: data.message || 'Erreur lors du renvoi' });
      }
    } catch (error) {
      console.error('Erreur:', error);
      setAlert({ severity: 'error', message: 'Erreur de connexion. Veuillez réessayer.' });
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Box>
      {/* En-tête */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h5" component="h2" gutterBottom color="primary.main">
          🔢 Code de vérification
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Étape 2/4 : Saisissez le code reçu par email
        </Typography>
      </Box>

      {/* Info email */}
      <Paper sx={{ p: 2, mb: 3, backgroundColor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
        <Typography variant="body2" align="center" color="primary.main">
          Un code à 6 chiffres a été envoyé à <strong>{resetData.email}</strong>
        </Typography>
      </Paper>

      {/* Timer */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <Chip
          icon={<Timer />}
          label={`Expire dans : ${formatTime(timeLeft)}`}
          color={timeLeft < 60 ? 'error' : 'primary'}
          variant="outlined"
        />
      </Box>

      {/* Alerte */}
      {alert && (
        <Alert 
          severity={alert.severity} 
          sx={{ mb: 3 }}
          onClose={() => setAlert(null)}
        >
          {alert.message}
        </Alert>
      )}

      {/* Formulaire OTP */}
      <Paper 
        component="form" 
        onSubmit={handleSubmit}
        sx={{ 
          p: 3, 
          backgroundColor: 'grey.50',
          border: '1px solid',
          borderColor: 'grey.200'
        }}
      >
        {/* Inputs OTP */}
        <Grid container spacing={1} justifyContent="center" sx={{ mb: 4 }}>
          {otp.map((digit, index) => (
            <Grid item key={index}>
              <TextField
                inputRef={(el) => inputRefs.current[index] = el}
                value={digit}
                onChange={(e) => handleOtpChange(e.target.value, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onPaste={handlePaste}
                inputProps={{
                  maxLength: 1,
                  style: { 
                    textAlign: 'center', 
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    width: '3rem',
                    height: '3rem',
                    padding: 0
                  }
                }}
                disabled={loading}
                sx={{
                  width: 60,
                  '& .MuiOutlinedInput-root': {
                    height: 60,
                  }
                }}
              />
            </Grid>
          ))}
        </Grid>

        {/* Boutons */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading || timeLeft === 0 || otp.join('').length !== 6}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Verified />}
            sx={{
              py: 1.5,
              background: '#339669',
              '&:hover': {
                background: '#339669',
              },
            }}
          >
            {loading ? 'Vérification...' : 'Vérifier le code'}
          </Button>

          <Button
            fullWidth
            variant="outlined"
            size="large"
            onClick={handleResend}
            disabled={resendLoading || timeLeft > 0}
            startIcon={resendLoading ? <CircularProgress size={20} /> : <Refresh />}
            sx={{ py: 1.5 }}
          >
            {resendLoading ? 'Envoi...' : 'Renvoyer le code'}
          </Button>
        </Box>
      </Paper>

      {/* Lien retour */}
      <Box sx={{ textAlign: 'center', mt: 3 }}>
        <Link
          component="button"
          variant="body2"
          onClick={onBack}
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            gap: 1,
            color: '#2E7D32',
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' }
          }}
        >
          <ArrowBack fontSize="small" />
          Retour à l'étape précédente
        </Link>
      </Box>
    </Box>
  );
};

export default OTPVerification;